import React, { ReactElement, useContext, useEffect, useState } from "react";
import { IconButton, Snackbar } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { UserType } from "../../graphql/types";
import { UserTypeContext } from "../../UserTypeContext";
import { isApolloErrorLike } from "../../apolloErrorReporting";
import { useEnumStrings } from "../enumStrings";
import { getUserFacingErrorText } from "./utilities/userFacingError";

// Wave 1 (Oct 19 2026): a failed action (mutation) no longer opens the general
// error page, so the user keeps their work. A screen that does not show its
// own error gets this notice instead, on the same screen.

const ACTION_ERROR_EVENT = "cmp-action-error-notice";

/** Shows the app-wide notice for an action error that the screen does not show itself. */
export const showActionErrorNotice = (error: unknown): void => {
  window.dispatchEvent(new CustomEvent(ACTION_ERROR_EVENT, { detail: { error } }));
};

/**
 * A screen that does not catch a failed action leaves an unhandled promise
 * rejection. For an Apollo error, show the notice. Returns the remover.
 */
export const listenForUnhandledActionErrors = (target: Window): (() => void) => {
  const handler = (event: PromiseRejectionEvent) => {
    if (!isApolloErrorLike(event.reason)) return;
    event.preventDefault();
    showActionErrorNotice(event.reason);
  };
  target.addEventListener("unhandledrejection", handler);
  return () => target.removeEventListener("unhandledrejection", handler);
};

const ActionErrorNotice = (): ReactElement => {
  const enumStrings = useEnumStrings();
  const userType = useContext(UserTypeContext)?.state?.userType;
  const [notice, setNotice] = useState<{ error: unknown; id: number }>();

  useEffect(() => {
    const removeUnhandled = listenForUnhandledActionErrors(window);
    const onNotice = (event: Event) =>
      setNotice({ error: (event as CustomEvent<{ error: unknown }>).detail?.error, id: Date.now() });
    window.addEventListener(ACTION_ERROR_EVENT, onNotice);
    return () => {
      removeUnhandled();
      window.removeEventListener(ACTION_ERROR_EVENT, onNotice);
    };
  }, []);

  // Members contact their chamber; chamber staff contact CMP support.
  const fallback =
    userType === UserType.ChamberMemberAdmin
      ? enumStrings.userFacingErrors.actionFailedMember
      : enumStrings.userFacingErrors.actionFailedStaff;
  const text = notice ? getUserFacingErrorText(notice.error, fallback) : "";

  return (
    <Snackbar
      key={notice?.id}
      open={Boolean(notice)}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      onClose={(_event, reason) => {
        if (reason !== "clickaway") setNotice(undefined);
      }}
      message={<span role="alert">{text}</span>}
      action={
        <IconButton
          size="small"
          color="inherit"
          aria-label={enumStrings.userFacingErrors.close}
          onClick={() => setNotice(undefined)}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      }
    />
  );
};

export default ActionErrorNotice;
